import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Store } from "lucide-react";

export default function PayOutDialog({ open, onOpenChange, customer, balance, onDone }) {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [shopId, setShopId] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [shops, setShops] = useState([]);
  const payable = balance?.payable || 0;

  useEffect(() => {
    if (open) {
      setAmount(payable > 0 ? String(payable) : "");
      setMode("cash");
      setShopId("");
      setNotes("");
      // Load shops
      api.get("/shops").then(({ data }) => {
        setShops(data.items || []);
        if (data.items?.length === 1) {
          setShopId(data.items[0].id);
        }
      }).catch(() => setShops([]));
    }
  }, [open, payable]);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (!shopId) return toast.error("Please select a shop");
    if (amt - payable > 0.01) return toast.error(`Amount exceeds available payable ₹${payable.toFixed(2)}`);
    setBusy(true);
    try {
      await api.post(`/credit/${customer.id}/payout`, {
        amount: amt,
        shop_id: shopId,
        payment_mode: mode,
        notes,
      });
      toast.success(`Payout of ₹${amt.toFixed(2)} recorded`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="payout-dialog">
        <DialogHeader>
          <DialogTitle>Pay Out / Settle Store Credit</DialogTitle>
          <DialogDescription>
            Hand cash back to <span className="font-medium">{customer?.name}</span> from their store credit balance. Available payable:{" "}
            <span className="font-mono-data text-emerald-700">₹{payable.toFixed(2)}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs flex items-center gap-1">
              <Store className="w-3 h-3" /> Shop Paying Out *
            </Label>
            <Select value={shopId} onValueChange={setShopId}>
              <SelectTrigger className="mt-1" data-testid="payout-shop-select">
                <SelectValue placeholder="Select shop paying the customer" />
              </SelectTrigger>
              <SelectContent>
                {shops.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">
              This payout will be recorded against the selected shop
            </p>
          </div>
          <div>
            <Label className="text-xs">Amount *</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 font-mono-data"
              placeholder="0.00"
              data-testid="payout-amount-input"
            />
          </div>
          <div>
            <Label className="text-xs">Payment Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="mt-1" data-testid="payout-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="card">Card</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" rows={2} placeholder="Optional" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !amount || !shopId} style={{ backgroundColor: "#9E6D5B" }} className="text-white" data-testid="payout-submit">
            {busy ? "Saving…" : "Pay Out"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
